import { supabase } from '../../services/supabase';
import { getStaffPositions } from '../../services/positions';
import { isKeystoneLeader, isKeystoneMember } from '../../services/keystone/permissions';
import type { RawRoles } from './deriveRoles';

/**
 * Loads every role fact for one user in parallel. Each source fails closed on
 * its own (false / []), matching what the separate hooks did before (#13).
 */
export async function loadRawRoles(uid: string): Promise<RawRoles & { positionsError: string | null }> {
  const profile = (async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_admin, is_active')
        .eq('id', uid)
        .single();
      if (error) {
        console.error('Error fetching admin status:', error);
        return { isAdmin: false, isActive: false };
      }
      return { isAdmin: data?.is_admin || false, isActive: data?.is_active || false };
    } catch (err) {
      console.error('Error checking admin status:', err);
      return { isAdmin: false, isActive: false };
    }
  })();

  const positions = getStaffPositions({ staffId: uid, activeOnly: true })
    .then((rows) => ({ rows, error: null as string | null }))
    .catch((err) => {
      console.error('Error fetching staff positions:', err);
      return { rows: [], error: err instanceof Error ? err.message : 'Failed to fetch positions' };
    });

  // isKeystoneMember/Leader already fail closed; the catch mirrors useKeystoneAccess.
  const keystone = Promise.all([isKeystoneMember(uid), isKeystoneLeader(uid)]).catch((err) => {
    console.error('useKeystoneAccess failed:', err);
    return [false, false] as [boolean, boolean];
  });

  const [p, pos, [member, leader]] = await Promise.all([profile, positions, keystone]);
  return {
    ...p,
    positions: pos.rows,
    positionsError: pos.error,
    keystoneMember: member,
    keystoneLeader: leader,
  };
}
